/**
 * Org chart — top-down reporting diagram built from employee `managerId`.
 *
 * Prefers the Active directory (all authenticated roles) so Employees can see
 * the full Active hierarchy. Falls back to the role-scoped employee list when
 * the directory is unavailable.
 *
 * Roots: no manager, missing manager, self-manager, or cycle break.
 * Clicking a name navigates to `/employees/:id`.
 * Nodes with reports expand/collapse; toolbar expands/collapses the full tree.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronRight, Network, Users } from 'lucide-react';
import { useEmployeeDirectory, useEmployees } from '../api.js';
import { Button } from '../../../shell/Button.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { PersonAvatar, employeeAvatarSrc } from '../../../shell/PersonAvatar.js';
import {
  buildOrgChart,
  collectExpandableIds,
  countEmployeeNodes,
  defaultExpandedIds,
  type OrgChartNode,
  type OrgPerson,
} from '../lib/org-chart.js';

export function OrgChartPage() {
  const directoryQuery = useEmployeeDirectory();
  const listQuery = useEmployees(
    { limit: 1000 },
    { enabled: directoryQuery.isError || (!directoryQuery.isLoading && !directoryQuery.data) },
  );

  const usingDirectory = Boolean(directoryQuery.data?.items);
  const people: OrgPerson[] | undefined = usingDirectory
    ? directoryQuery.data!.items
    : listQuery.data?.items;

  const isLoading = directoryQuery.isLoading || (!usingDirectory && listQuery.isLoading);
  const isError = !people && !isLoading && (directoryQuery.isError || listQuery.isError);

  const chart = useMemo(() => (people ? buildOrgChart(people) : null), [people]);
  const total = people?.length ?? 0;
  const expandableIds = useMemo(
    () => (chart ? collectExpandableIds(chart) : []),
    [chart],
  );

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const membershipKey = useMemo(
    () => (people ? people.map((p) => p.id).sort().join(',') : ''),
    [people],
  );

  // Seed / re-seed defaults when the set of people changes (not on every render).
  useEffect(() => {
    if (!chart) {
      setExpandedIds(new Set());
      return;
    }
    setExpandedIds(defaultExpandedIds(chart));
  }, [membershipKey]); // eslint-disable-line react-hooks/exhaustive-deps -- chart synced via membershipKey

  const expandAll = () => setExpandedIds(new Set(expandableIds));
  const collapseAll = () => setExpandedIds(new Set());

  const toggleNode = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Org chart</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {isLoading
              ? 'Loading reporting structure…'
              : `${total} ${usingDirectory ? 'active ' : ''}employee${total === 1 ? '' : 's'} · Top-down reporting structure`}
          </p>
        </div>
        {chart && expandableIds.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={expandAll}>
              Expand all
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={collapseAll}>
              Collapse all
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-slate-400 dark:text-slate-500">Loading…</div>
      ) : isError ? (
        <EmptyState
          icon={<Network className="h-8 w-8" />}
          title="Could not load org chart"
          description="Try refreshing the page."
        />
      ) : !chart ? (
        <EmptyState
          icon={<Users className="h-8 w-8" />}
          title="No employees to show"
          description="Once employees are added with manager relationships, the reporting tree will appear here."
        />
      ) : (
        <div className="org-diagram-canvas rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <div className="org-diagram" role="tree" aria-label="Organization chart">
            <ul className="org-diagram__level org-diagram__level--root">
              <OrgDiagramNode
                node={chart}
                depth={0}
                expandedIds={expandedIds}
                onToggle={toggleNode}
              />
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function OrgDiagramNode({
  node,
  depth,
  expandedIds,
  onToggle,
}: {
  node: OrgChartNode;
  depth: number;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const open = hasChildren && expandedIds.has(node.id);
  const label = node.kind === 'employee' ? node.person.name : 'Unassigned';
  const directReports = node.kind === 'employee'
    ? node.children.filter((child) => child.kind === 'employee').length
    : 0;
  const nestedTotal = node.children.reduce(
    (total, child) => total + countEmployeeNodes(child),
    0,
  );
  const childLevelClassName = depth === 0
    ? 'org-diagram__level org-diagram__level--branches'
    : 'org-diagram__level org-diagram__level--descendants';

  return (
    <li className="org-diagram__node" role="treeitem" aria-expanded={hasChildren ? open : undefined}>
      <div className={node.kind === 'unassigned'
        ? 'org-diagram__card org-diagram__card--unassigned'
        : 'org-diagram__card'}>
        {node.kind === 'employee' ? (
          <>
            <PersonAvatar
              name={node.person.name}
              src={employeeAvatarSrc(node.person.id, node.person.avatarStorageKey)}
              size="sm"
            />
            <div className="org-diagram__meta">
              <Link
                to={`/employees/${node.person.id}`}
                className="org-diagram__name"
                title={node.person.name}
              >
                {node.person.name}
              </Link>
              <p className="org-diagram__title">
                {node.person.title}
                {node.person.department ? ` · ${node.person.department}` : ''}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="org-diagram__unassigned-icon" aria-hidden>
              <Users className="h-5 w-5" />
            </div>
            <div className="org-diagram__meta">
              <p className="org-diagram__name">Unassigned</p>
              <p className="org-diagram__title">
                {nestedTotal} employee{nestedTotal === 1 ? '' : 's'} outside the head&apos;s reporting tree
              </p>
            </div>
          </>
        )}
        {hasChildren && (
          <button
            type="button"
            className="org-diagram__toggle"
            aria-label={open ? `Collapse reports of ${label}` : `Expand reports of ${label}`}
            onClick={() => onToggle(node.id)}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span>
              {node.kind === 'employee'
                ? `${directReports} report${directReports === 1 ? '' : 's'}`
                : `${nestedTotal} unassigned`}
              {node.kind === 'employee' && nestedTotal !== directReports ? ` · ${nestedTotal}` : ''}
            </span>
          </button>
        )}
      </div>

      {hasChildren && open && (
        <ul className={childLevelClassName} role="group">
          {node.children.map((child) => (
            <OrgDiagramNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
